import { observable, action, computed } from "mobx";

import { BaseStore } from "./BaseStore";

import { HorseForSaleEndpoints } from "../endpoints/HorseForSaleEndpoints";
import { AttachmentLogEndpoints } from "../endpoints/AttachmentLogEndpoints";

import { HorseForSale, GeocodedHorseForSale } from "../model/HorseForSale";
import { ListWrapper, EmptyWrapper } from "../model/ListWrapper";

import { PatchRequest, MultiPatchRequest } from "../requests/PatchRequests";
import { OutcomeRequest, StableReleaseRequest, WithdrawalRequest } from "src/requests/HorseForSaleRequests";

export class HorseForSaleStore extends BaseStore {
    @observable private _horseForSaleId: number;
    @observable private _horseForSale: HorseForSale;

    constructor(private horseForSaleEndpoints: HorseForSaleEndpoints, private attachmentLogEndpoints: AttachmentLogEndpoints) {
        super();
    }

    @action async updateSubjectId(id: number) {
        this._horseForSaleId = id;
    }

    @action async refresh(): Promise<HorseForSale> {
        this.setLoading();
        try {
            this._horseForSale = await this.horseForSaleEndpoints.getHorseForSale(this._horseForSaleId);
            this.setReady();
            return this._horseForSale;
        } catch (e) {
            this.setError(e ? e.message || e : "An unknown error occurred");
            throw e;
        }
    }

    @action updateFieldsByObject(fields: { [key: string]: string | number | null }): Promise<HorseForSale> {
        const requests = Object.keys(fields).map((key) => {
            return { field: key, value: fields[key] === null ? null : `${fields[key]}` };
        });
        return this.updateFields(requests);
    }

    @action async updateFields(fields: Array<PatchRequest>): Promise<HorseForSale> {
        this.setLoading();
        try {
            const horseForSale = await this.horseForSaleEndpoints.editHorseForSale(this._horseForSaleId, { requests: fields });
            this._horseForSale = horseForSale;
            return horseForSale;
        } catch (e) {
            // Nothing to handle here, pass it back
            throw e;
        } finally {
            this.setReady();
        }
    }

    /**
     * @deprecated
     * TODO: Remove this
     */
    @action horseForSaleUpdateFieldsByObject(horseForSaleId: number, fields: { [key: string]: string | number | null }): Promise<HorseForSale> {
        const requests = Object.keys(fields).map((key) => {
            return { field: key, value: fields[key] === null ? null : `${fields[key]}` };
        });
        return this.horseForSaleUpdateFields(horseForSaleId, requests);
    }

    /**
     * @deprecated
     * TODO: Remove this
     */
    @action async horseForSaleUpdateFields(horseForSaleId: number, fields: Array<PatchRequest>): Promise<HorseForSale> {
        this.setLoading();
        try {
            const horseForSale = await this.horseForSaleEndpoints.editHorseForSale(horseForSaleId, { requests: fields });
            this._horseForSale = horseForSale;
            return horseForSale;
        } catch (e) {
            // Nothing to handle here, pass it back
            throw e;
        } finally {
            this.setReady();
        }
    }

    @computed get horseForSale(): HorseForSale | undefined {
        return this._horseForSale;
    }

    @action async addAttachment(fileId: number, fileName: string, type: string, fileBase64: string, markCompliant: boolean): Promise<HorseForSale> {
        this.setLoading();
        try {
            const attachment = await this.attachmentLogEndpoints.addAttachment(fileId, [
                { name: fileName, contentType: type, data: fileBase64, markCompliant }
            ]);
            return this.refresh();
        } catch (e) {
            throw e;
        } finally {
            this.setReady();
        }
    }

    @action async deleteAttachment(fileId: number): Promise<HorseForSale> {
        this.setLoading();
        try {
            const attachment = await this.attachmentLogEndpoints.deleteAttachment(fileId);
            return this.refresh();
        } catch (e) {
            throw e;
        } finally {
            this.setReady();
        }
    }

    @action async markLogCompliant(attachmentLogId: number, notes: string): Promise<HorseForSale> {
        this.setLoading();
        try {
            const attachment = await this.attachmentLogEndpoints.markLogCompliant(attachmentLogId, { notes: notes });
            return this.refresh();
        } catch (e) {
            // Nothing to handle here, pass it back
            throw e;
        } finally {
            this.setReady();
        }
    }

    @action async markLogNotCompliant(attachmentLogId: number): Promise<HorseForSale> {
        this.setLoading();
        try {
            const attachment = await this.attachmentLogEndpoints.markLogNotCompliant(attachmentLogId);
            return this.refresh();
        } catch (e) {
            // Nothing to handle here, pass it back
            throw e;
        } finally {
            this.setReady();
        }
    }

    /**
     * @deprecated
     * TODO: Remove this (go straight to endpoints instead). This method doesn't have any effect
     * on the store's state so it doesn't need to be here.
     */
    @action async generateHipNumbers(horseSaleId: number) {
        this.setLoading();
        try {
            await this.horseForSaleEndpoints.generateHipNumbers(horseSaleId);
            // TODO: Should there be some sort of state refresh here?
        } catch (e) {
            // Nothing to handle here, pass it back
            throw e;
        } finally {
            this.setReady();
        }
    }

    /**
     * @deprecated
     * TODO: Remove this (go straight to endpoints instead). This method doesn't have any effect
     * on the store's state so it doesn't need to be here.
     */
    @action async swapHipNumbers(request: { [field: string]: number }): Promise<void> {
        this.setLoading();
        try {
            await this.horseForSaleEndpoints.swapHipNumbers(request);
        } catch (e) {
            // Nothing to handle here, pass it back
            throw e;
        } finally {
            this.setReady();
        }
    }

    /**
     * @deprecated
     * TODO: Remove this (go straight to endpoints instead). This method doesn't have any effect
     * on the store's state so it doesn't need to be here.
     */
    @action async getHorseForSaleCoordinatesList(fileId: number): Promise<ListWrapper<GeocodedHorseForSale>> {
        this.setLoading();
        try {
            const coodinates = await this.horseForSaleEndpoints.getHorseForSaleCoordinatesList(fileId);
            return coodinates;
        } catch (e) {
            throw e;
        } finally {
            this.setReady();
        }
    }

    @action async approveOrRejectEntryHorseForSale(newState: string, notes: string): Promise<HorseForSale> {
        this.setLoading();
        try {
            const horseForSale = await this.horseForSaleEndpoints.approveOrRejectEntryHorseForSale(this._horseForSaleId, { newState, notes });
            this._horseForSale = horseForSale;
            return horseForSale;
        } catch (e) {
            throw e;
        } finally {
            this.setReady();
        }
    }

    @action async generateRegistrationCertificateFeeInvoice(horseForSaleId: number): Promise<HorseForSale> {
        this.setLoading();
        try {
            await this.horseForSaleEndpoints.generateRegistrationCertificateFeeInvoice(horseForSaleId);
            return this.refresh();
        } catch (e) {
            throw e;
        } finally {
            this.setReady();
        }
    }

    @action async generateCogginsCertificateFeeInvoice(horseForSaleId: number): Promise<HorseForSale> {
        this.setLoading();
        try {
            await this.horseForSaleEndpoints.generateCogginsCertificateFeeInvoice(horseForSaleId);
            return this.refresh();
        } catch (e) {
            throw e;
        } finally {
            this.setReady();
        }
    }

    @action async withdrawHorseForSaleEarly(horseForSaleId: number, request: WithdrawalRequest): Promise<HorseForSale> {
        this.setLoading();
        try {
            const horseForSale = await this.horseForSaleEndpoints.withdrawHorseForSaleEarly(horseForSaleId, request);
            this._horseForSale = horseForSale;
            return horseForSale;
        } catch (e) {
            throw e;
        } finally {
            this.setReady();
        }
    }

    @action async editEarlyWithdrawalNotes(horseForSaleId: number, request: WithdrawalRequest): Promise<HorseForSale> {
        this.setLoading();
        try {
            const horseForSale = await this.horseForSaleEndpoints.editEarlyWithdrawalNotes(horseForSaleId, request);
            this._horseForSale = horseForSale;
            return horseForSale;
        } catch (e) {
            throw e;
        } finally {
            this.setReady();
        }
    }

    @action async registerOutcome(request: OutcomeRequest): Promise<HorseForSale> {
        this.setLoading();
        try {
            const horseForSale = await this.horseForSaleEndpoints.registerOutcome(this._horseForSaleId, request);
            this._horseForSale = horseForSale;
            return horseForSale;
        } catch (e) {
            throw e;
        } finally {
            this.setReady();
        }
    }

    @action async updateOutcome(request: OutcomeRequest): Promise<HorseForSale> {
        this.setLoading();
        try {
            const horseForSale = await this.horseForSaleEndpoints.updateOutcome(this._horseForSaleId, request);
            this._horseForSale = horseForSale;
            return horseForSale;
        } catch (e) {
            throw e;
        } finally {
            this.setReady();
        }
    }

    @action async generateEntryFeeInvoice(horseForSaleId: number): Promise<HorseForSale> {
        this.setLoading();
        try {
            await this.horseForSaleEndpoints.generateEntryFeeInvoice(horseForSaleId);
            this.refresh();
            return this._horseForSale;
        } catch (e) {
            throw e;
        } finally {
            this.setReady();
        }
    }

    @action async stableRelease(horseForSaleId: number, request: StableReleaseRequest): Promise<HorseForSale> {
        this.setLoading();
        try {
            const horseForSale = await this.horseForSaleEndpoints.stableRelease(horseForSaleId, request);
            return horseForSale;
        } catch (e) {
            throw e;
        } finally {
            this.setReady();
        }
    }
}



// WEBPACK FOOTER //
// ./src/stores/HorseForSaleStore.ts